import React, { useState } from 'react';
import { CalendarEvent, TimeSlot } from '../../types/calendar';
import { EventPill } from './EventPill';
import { Sparkles } from 'lucide-react';
interface CalendarMonthViewProps {
  events: CalendarEvent[];
  huecosDeOro: TimeSlot[];
  currentDate: Date;
  onSlotClick: (date: Date) => void;
  onEventClick: (event: CalendarEvent) => void;
}
export function CalendarMonthView({
  events,
  huecosDeOro,
  currentDate,
  onSlotClick,
  onEventClick
}: CalendarMonthViewProps) {
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const days = Array.from({
    length: 42
  }, (_, i) => {
    const dayNumber = i - startOffset + 1;
    const isCurrentMonth = dayNumber > 0 && dayNumber <= daysInMonth;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNumber);
    return {
      dayNumber,
      isCurrentMonth,
      date
    };
  });
  const getEventsForDay = (date: Date) => events.filter(e => {
    const eDate = new Date(e.start);
    return eDate.getDate() === date.getDate() && eDate.getMonth() === date.getMonth() && eDate.getFullYear() === date.getFullYear();
  });
  const getHuecosForDay = (date: Date) => huecosDeOro.filter(h => {
    const hDate = new Date(h.start);
    return hDate.getDate() === date.getDate() && hDate.getMonth() === date.getMonth() && hDate.getFullYear() === date.getFullYear();
  });
  const handleDragStart = (e: React.DragEvent, event: CalendarEvent) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    if (draggedEvent) {
      console.log(`Moved event ${draggedEvent.title} to ${date.toISOString()}`);
      alert('¿Confirmar nueva hora? (Simulado)');
      setDraggedEvent(null);
    }
  };
  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const today = new Date();
  return <div className="card overflow-hidden flex flex-col h-full" data-id="element-129">
      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-white/[0.06] bg-white/[0.02]" data-id="element-130">
        {weekDays.map((day, idx) => <div key={day} className={`py-3 text-center text-[11px] font-semibold uppercase tracking-[0.12em] ${idx >= 5 ? 'text-growthly-muted/70' : 'text-growthly-muted'}`} data-id="element-131">
            {day}
          </div>)}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 flex-1 auto-rows-fr" data-id="element-132">
        {days.map((day, idx) => {
        const dayEvents = day.isCurrentMonth ? getEventsForDay(day.date) : [];
        const dayHuecos = day.isCurrentMonth ? getHuecosForDay(day.date) : [];
        const hasEvents = dayEvents.length > 0;
        const isToday = day.isCurrentMonth && day.date.toDateString() === today.toDateString();
        const isWeekend = idx % 7 >= 5;
        return <div key={idx} className={`min-h-[110px] border-b border-r border-white/[0.04] p-2 relative transition-colors ${!day.isCurrentMonth ? 'bg-growthly-bg/30 opacity-40' : isToday ? 'bg-white/[0.03]' : hasEvents ? 'bg-white/[0.015]' : isWeekend ? 'bg-black/10 hover:bg-white/[0.02] cursor-pointer' : 'hover:bg-white/[0.02] cursor-pointer'}`} onClick={() => day.isCurrentMonth && onSlotClick(day.date)} onDragOver={handleDragOver} onDrop={e => day.isCurrentMonth && handleDrop(e, day.date)} data-id="element-133">
              {day.isCurrentMonth && <div className="flex items-center justify-between mb-1.5" data-id="element-134">
                  <span className={`text-[12px] font-semibold transition-all ${isToday ? 'bg-white text-[#0B0B0B] rounded-full w-6 h-6 flex items-center justify-center shadow-md' : isWeekend ? 'text-growthly-muted' : 'text-growthly-text-2'}`} data-id="element-135">
                    {day.dayNumber}
                  </span>
                  {dayHuecos.length > 0 && dayEvents.length === 0 && <Sparkles className="w-3 h-3 text-growthly-green/60" aria-label="Hueco de oro disponible" data-id="element-136" />}
                </div>}

              <div className="space-y-1" data-id="element-137">
                {dayEvents.slice(0, 3).map(event => <div key={event.id} draggable onDragStart={e => handleDragStart(e, event)} className="cursor-grab active:cursor-grabbing" data-id="element-138">
                    <EventPill event={event} onClick={e => {
                e.stopPropagation();
                onEventClick(event);
              }} data-id="element-139" />
                  </div>)}
                {dayEvents.length > 3 && <div className="text-[10px] text-growthly-muted px-1 font-medium" data-id="element-140">
                    +{dayEvents.length - 3} más
                  </div>}
              </div>

              {dayHuecos.length > 0 && dayEvents.length === 0 && <div className="absolute inset-2 mt-7 border border-dashed border-growthly-green/20 bg-growthly-green/[0.04] rounded-md flex items-center justify-center pointer-events-none" title="Slot recomendado según disponibilidad y conversión histórica" data-id="element-141">
                  <span className="text-[10px] font-medium text-growthly-green/70 uppercase tracking-wide" data-id="element-142">
                    Disponible
                  </span>
                </div>}
            </div>;
      })}
      </div>
    </div>;
}